import React, { useState, useEffect, useCallback, useRef } from "react";
import { Form, Input, Select, InputNumber, Switch, Button, Tabs, Space, Typography, Divider, Tag, Row, Col, Tooltip, Descriptions } from "antd";
import { PlusOutlined, DeleteOutlined, SaveOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import DetailDrawer from "../../../../components/shared/DetailDrawer";
import { DataTable } from "../../../../components/shared";
import { MedicationCatalogItem, MedicationInteraction, DosageGuide, defaultCatalogItem, MEDICATION_CATEGORIES, FREQUENCY_OPTIONS, ADMINISTRATION_ROUTES, AU_SCHEDULES, INTERACTION_SEVERITIES, EVIDENCE_LEVEL_LABELS } from "../../../../types/healthcare/MedicationCatalogTypes";
import { createCatalogMedication, updateCatalogMedication } from "../../../../api/healthcare/MedicationCatalogApi";
import { useNotifications } from "../../../../context/NotificationContext";

const { TextArea } = Input;
const { Text } = Typography;

type InteractionRow = MedicationInteraction & { _key: number };

type DosageGuideRow = DosageGuide & { _key: number };

type PatientProfileFlag = { category: string; name: string; relevance: string; risk_level: 'high' | 'moderate' | 'low' };
type ProfileFlagRow = PatientProfileFlag & { _key: number };

interface Props {
	open: boolean;
	item: Partial<MedicationCatalogItem> | null;
	onClose: () => void;
	onSaved: (item: MedicationCatalogItem) => void;
	company: string;
}

const MedicationCatalogDrawer: React.FC<Props> = ({ open, item, onClose, onSaved, company }) => {
	const [form, setForm] = useState<Partial<MedicationCatalogItem>>({ ...defaultCatalogItem });
	const [interactions, setInteractions] = useState<InteractionRow[]>([]);
	const [newInteraction, setNewInteraction] = useState<Partial<MedicationInteraction>>({});
	const [dosageGuides, setDosageGuides] = useState<DosageGuideRow[]>([]);
	const [newDosageGuide, setNewDosageGuide] = useState<Partial<DosageGuide>>({});
	const [profileFlags, setProfileFlags] = useState<ProfileFlagRow[]>([]);
	const [newProfileFlag, setNewProfileFlag] = useState<Partial<PatientProfileFlag>>({});
	const [loading, setLoading] = useState(false);
	const [activeTab, setActiveTab] = useState("general");
	const [formErrors, setFormErrors] = useState<string[]>([]);
	const keyCounter = useRef(0);
	const { showSuccess, showError } = useNotifications();

	const isEdit = !!item?.id;
	const isGlobal = !!item?.admin_medication_id;

	useEffect(() => {
		if (item) {
			setForm({ ...defaultCatalogItem, ...item });
			setInteractions((item.interactions ?? []).map((ix, i) => ({ ...ix, _key: i })));
			setProfileFlags((item.patient_profile_flags ?? []).map((f, i) => ({ ...f, _key: i })));
			setDosageGuides((item.dosage_guides ?? []).map((g, i) => ({ ...g, _key: i })));
		} else {
			setForm({ ...defaultCatalogItem, company });
			setInteractions([]);
			setProfileFlags([]);
			setDosageGuides([]);
		}
		setNewInteraction({});
		setNewProfileFlag({});
		setNewDosageGuide({});
		setActiveTab("general");
		setFormErrors([]);
		keyCounter.current = 0;
	}, [item, company]);

	const set = useCallback((key: keyof MedicationCatalogItem, val: any) => {
		setForm((prev) => ({ ...prev, [key]: val }));
	}, []);

	const addInteraction = () => {
		if (!newInteraction.name || !newInteraction.severity) return;
		setInteractions((prev) => [...prev, { ...(newInteraction as MedicationInteraction), _key: ++keyCounter.current }]);
		setNewInteraction({});
	};

	const removeInteraction = (key: number) => {
		setInteractions((prev) => prev.filter((ix) => ix._key !== key));
	};

	const addDosageGuide = () => {
		const label = (newDosageGuide.label ?? "").trim();
		const dosage = (newDosageGuide.dosage ?? "").trim();
		if (!label || !dosage) return;
		if (dosageGuides.some((g) => g.label.trim() === label)) {
			setFormErrors([`A dosage guide with the label "${label}" already exists`]);
			return;
		}
		setFormErrors([]);
		setDosageGuides((prev) => [...prev, { ...(newDosageGuide as DosageGuide), label, dosage, _key: ++keyCounter.current }]);
		setNewDosageGuide({});
	};

	const removeDosageGuide = (key: number) => {
		setDosageGuides((prev) => prev.filter((g) => g._key !== key));
	};

	const setDefaultDosageGuide = (key: number) => {
		setDosageGuides((prev) => prev.map((g) => ({ ...g, is_default: g._key === key })));
	};

	const addProfileFlag = () => {
		if (!newProfileFlag.name || !newProfileFlag.category || !newProfileFlag.risk_level) return;
		setProfileFlags((prev) => [...prev, { ...(newProfileFlag as PatientProfileFlag), _key: ++keyCounter.current }]);
		setNewProfileFlag({});
	};

	const removeProfileFlag = (key: number) => {
		setProfileFlags((prev) => prev.filter((f) => f._key !== key));
	};

	const handleSave = async () => {
		if (!form.name?.trim()) {
			setFormErrors(["Medication name is required"]);
			return;
		}
		setFormErrors([]);
		setLoading(true);
		try {
			const payload = { ...form, company: form.company ?? company, interactions: interactions.map(({ _key, ...rest }) => rest), patient_profile_flags: profileFlags.map(({ _key, ...rest }) => rest), dosage_guides: dosageGuides.map(({ _key, ...rest }) => rest) };
			const saved = isEdit ? await updateCatalogMedication(item!.id!, payload) : await createCatalogMedication(payload);
			showSuccess(isEdit ? "Medication updated" : "Medication created");
			onSaved(saved);
		} catch (e) {
			showError("Failed to save medication");
		} finally {
			setLoading(false);
		}
	};

	const interactionColumns: ColumnsType<InteractionRow> = [
		{ title: "Drug", dataIndex: "name", key: "name" },
		{
			title: "Severity",
			dataIndex: "severity",
			key: "severity",
			render: (s: string) => {
				const color = s === "severe" ? "var(--mg-danger)" : s === "moderate" ? "var(--mg-warning)" : "var(--mg-info)";
				const bg = s === "severe" ? "var(--mg-danger-bg)" : s === "moderate" ? "var(--mg-warning-bg)" : "var(--mg-info-bg)";
				return <Tag style={{ borderColor: color, background: bg, color }}>{s.charAt(0).toUpperCase() + s.slice(1)}</Tag>;
			},
		},
		{ title: "Description", dataIndex: "description", key: "description", ellipsis: true },
		{
			title: "",
			key: "del",
			width: 40,
			render: (_: any, r: InteractionRow) => (
				<Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={(e) => { e.stopPropagation(); removeInteraction(r._key); }} />
			),
		},
	];

	const profileFlagColumns: ColumnsType<ProfileFlagRow> = [
		{ title: "Category", dataIndex: "category", key: "category", width: 120 },
		{ title: "Name", dataIndex: "name", key: "name" },
		{ title: "Relevance", dataIndex: "relevance", key: "relevance", ellipsis: true },
		{
			title: "Risk",
			dataIndex: "risk_level",
			key: "risk_level",
			width: 90,
			render: (s: string) => {
				const color = s === "high" ? "var(--mg-danger)" : s === "moderate" ? "var(--mg-warning)" : "var(--mg-info)";
				const bg = s === "high" ? "var(--mg-danger-bg)" : s === "moderate" ? "var(--mg-warning-bg)" : "var(--mg-info-bg)";
				return <Tag style={{ borderColor: color, background: bg, color }}>{s.charAt(0).toUpperCase() + s.slice(1)}</Tag>;
			},
		},
		{
			title: "",
			key: "del",
			width: 40,
			render: (_: any, r: ProfileFlagRow) => (
				<Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={(e) => { e.stopPropagation(); removeProfileFlag(r._key); }} />
			),
		},
	];

	const anyGuideDefault = dosageGuides.some((g) => g.is_default);
	const isEffectiveDefault = (r: DosageGuideRow, index: number) => !!r.is_default || (!anyGuideDefault && index === 0);

	const dosageGuideColumns: ColumnsType<DosageGuideRow> = [
		{
			title: "Label",
			dataIndex: "label",
			key: "label",
			render: (_: any, r: DosageGuideRow, index: number) => (
				<Space size={4}>
					<Text strong>{r.label}</Text>
					{isEffectiveDefault(r, index) && (
						<Tag style={{ borderColor: "var(--mg-primary-border)", background: "var(--mg-primary-bg)", color: "var(--mg-primary)" }}>Default</Tag>
					)}
				</Space>
			),
		},
		{ title: "Description", dataIndex: "description", key: "description", ellipsis: true, render: (v: string) => v || <Text type="secondary">—</Text> },
		{ title: "Dosage", dataIndex: "dosage", key: "dosage" },
		{ title: "Frequency", dataIndex: "frequency", key: "frequency", render: (v: string) => v || <Text type="secondary">—</Text> },
		{ title: "Route", dataIndex: "administration_route", key: "administration_route", render: (v: string) => v || <Text type="secondary">—</Text> },
		{ title: "Duration", dataIndex: "duration_days", key: "duration_days", width: 90, render: (v: number | null) => (v ? `${v} days` : <Text type="secondary">—</Text>) },
		{ title: "Refills", dataIndex: "refills_authorized", key: "refills_authorized", width: 80, render: (v: number | null) => (v === null || v === undefined ? <Text type="secondary">—</Text> : v) },
		{
			title: "",
			key: "setdefault",
			width: 100,
			render: (_: any, r: DosageGuideRow, index: number) => {
				if (isGlobal || isEffectiveDefault(r, index)) return null;
				return (
					<Button type="link" size="small" onClick={(e) => { e.stopPropagation(); setDefaultDosageGuide(r._key); }}>
						Set default
					</Button>
				);
			},
		},
		{
			title: "",
			key: "del",
			width: 40,
			render: (_: any, r: DosageGuideRow) =>
				isGlobal ? null : <Button type="text" danger size="small" icon={<DeleteOutlined />} onClick={(e) => { e.stopPropagation(); removeDosageGuide(r._key); }} />,
		},
	];

	const tabs = [
		{
			key: "general",
			label: "General",
			children: (
				<Form layout="vertical" component="div">
				<Row gutter={[16, 0]}>
					<Col span={24}>
						<Form.Item
							label={
								<>
									<Text strong>Name</Text> <Text type="danger">*</Text>
								</>
							}
						>
							<Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g., Metformin" disabled={isGlobal} />
						</Form.Item>
					</Col>
					<Col xs={24} md={12}>
						<Form.Item label="Generic Name">
							<Input value={form.generic_name} onChange={(e) => set("generic_name", e.target.value)} placeholder="INN / generic name" disabled={isGlobal} />
						</Form.Item>
					</Col>
					<Col xs={24} md={12}>
						<Form.Item label="Drug Class">
							<Input value={form.drug_class} onChange={(e) => set("drug_class", e.target.value)} placeholder="e.g., Biguanide" disabled={isGlobal} />
						</Form.Item>
					</Col>
					<Col xs={24} md={12}>
						<Form.Item label="Category">
							<Select value={form.medication_category} onChange={(v) => set("medication_category", v)} options={MEDICATION_CATEGORIES} placeholder="Select category" allowClear disabled={isGlobal} />
						</Form.Item>
					</Col>
					<Col xs={24} md={12}>
						<Form.Item label="AU Schedule">
							<Select value={form.schedule} onChange={(v) => set("schedule", v)} options={AU_SCHEDULES} placeholder="Select schedule" allowClear disabled={isGlobal} />
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item label="Brand Names">
							<Select mode="tags" value={form.brand_names ?? []} onChange={(v) => set("brand_names", v)} placeholder="Type brand name and press Enter" open={false} disabled={isGlobal} />
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item label="Description">
							<TextArea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} placeholder="Brief description" disabled={isGlobal} />
						</Form.Item>
					</Col>
					<Divider style={{ margin: "4px 0 16px" }}>Flags</Divider>
					<Col xs={24} md={8}>
						<Form.Item label="High Risk">
							<Switch checked={form.high_risk} onChange={(v) => set("high_risk", v)} disabled={isGlobal} />
						</Form.Item>
					</Col>
					<Col xs={24} md={8}>
						<Form.Item label="Controlled Substance">
							<Switch checked={form.controlled_substance} onChange={(v) => set("controlled_substance", v)} disabled={isGlobal} />
						</Form.Item>
					</Col>
					<Col xs={24} md={8}>
						<Form.Item label="Requires Script">
							<Switch checked={form.requires_script} onChange={(v) => set("requires_script", v)} disabled={isGlobal} />
						</Form.Item>
					</Col>
					<Col xs={24} md={8}>
						<Form.Item label="Active">
							<Switch checked={form.is_active} onChange={(v) => set("is_active", v)} disabled={isGlobal} />
						</Form.Item>
					</Col>
				</Row>
				</Form>
			),
		},
		{
			key: "defaults",
			label: "Dosage Guides & Coding",
			children: (
				<Form layout="vertical" component="div">
				<Row gutter={[16, 0]}>
					<Col span={24}>
						<Divider style={{ margin: "4px 0 8px" }}>Dosage Guides</Divider>
						<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
							Define one or more dosing regimens. The default guide pre-fills prescriptions and treatment-plan steps.
						</Text>
						{!isGlobal && (
							<>
								<Space style={{ marginBottom: 8, width: "100%", flexWrap: "wrap" }} align="start">
									<Input placeholder="Label *" style={{ width: 180 }} value={newDosageGuide.label ?? ""} onChange={(e) => setNewDosageGuide((p) => ({ ...p, label: e.target.value }))} />
									<Input placeholder="Dosage *" style={{ width: 130 }} value={newDosageGuide.dosage ?? ""} onChange={(e) => setNewDosageGuide((p) => ({ ...p, dosage: e.target.value }))} />
									<Select placeholder="Frequency" style={{ width: 150 }} value={newDosageGuide.frequency ?? undefined} onChange={(v) => setNewDosageGuide((p) => ({ ...p, frequency: v }))} options={FREQUENCY_OPTIONS} allowClear />
									<Select placeholder="Route" style={{ width: 140 }} value={newDosageGuide.administration_route ?? undefined} onChange={(v) => setNewDosageGuide((p) => ({ ...p, administration_route: v }))} options={ADMINISTRATION_ROUTES} allowClear />
									<InputNumber placeholder="Duration (days)" style={{ width: 130 }} min={1} value={newDosageGuide.duration_days ?? undefined} onChange={(v) => setNewDosageGuide((p) => ({ ...p, duration_days: v ?? undefined }))} />
									<InputNumber placeholder="Refills" style={{ width: 90 }} min={0} value={newDosageGuide.refills_authorized ?? undefined} onChange={(v) => setNewDosageGuide((p) => ({ ...p, refills_authorized: v ?? undefined }))} />
								</Space>
								<TextArea
									placeholder="Description — what this regimen is for"
									style={{ marginBottom: 8 }}
									autoSize={{ minRows: 2, maxRows: 5 }}
									value={newDosageGuide.description ?? ""}
									onChange={(e) => setNewDosageGuide((p) => ({ ...p, description: e.target.value }))}
								/>
								<TextArea
									placeholder="Instructions — administration, reconstitution, storage, site rotation…"
									style={{ marginBottom: 8 }}
									autoSize={{ minRows: 3, maxRows: 10 }}
									value={newDosageGuide.instructions ?? ""}
									onChange={(e) => setNewDosageGuide((p) => ({ ...p, instructions: e.target.value }))}
								/>
								<Space style={{ marginBottom: 8 }}>
									<Button icon={<PlusOutlined />} onClick={addDosageGuide}>
										Add
									</Button>
								</Space>
							</>
						)}
						<DataTable
							bare
							dataSource={dosageGuides}
							rowKey={(r) => r._key}
							columns={dosageGuideColumns}
							pagination={false}
							emptyTitle="No dosage guides added"
							minHeight={80}
						/>
					</Col>
					<Divider style={{ margin: "16px 0 16px" }}>Coding & Integration</Divider>
					<Col xs={24} md={12}>
						<Form.Item label="NDC Code">
							<Input value={form.ndc_code} onChange={(e) => set("ndc_code", e.target.value)} placeholder="National Drug Code" disabled={isGlobal} />
						</Form.Item>
					</Col>
					<Col xs={24} md={12}>
						<Form.Item label="PBS Code">
							<Input value={form.pbs_code} onChange={(e) => set("pbs_code", e.target.value)} placeholder="AU PBS code" disabled={isGlobal} />
						</Form.Item>
					</Col>
					<Col xs={24} md={12}>
						<Form.Item label="MIMS ID">
							<Input value={form.mims_id} onChange={(e) => set("mims_id", e.target.value)} placeholder="MIMS reference ID" disabled={isGlobal} />
						</Form.Item>
					</Col>
					<Col xs={24} md={12}>
						<Form.Item label="e-Script Template ID">
							<Input value={form.escript_template_id} onChange={(e) => set("escript_template_id", e.target.value)} placeholder="e-scripting stub" disabled={isGlobal} />
						</Form.Item>
					</Col>
				</Row>
				</Form>
			),
		},
		{
			key: "clinical",
			label: "Clinical Data",
			children: (
				<Form layout="vertical" component="div">
				<Row gutter={[16, 0]}>
					<Col span={24}>
						<Form.Item label="Contraindications">
							<Select mode="tags" value={form.contraindications ?? []} onChange={(v) => set("contraindications", v)} placeholder="Type a contraindication and press Enter" open={false} disabled={isGlobal} />
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item label="Side Effects">
							<Select mode="tags" value={form.side_effects ?? []} onChange={(v) => set("side_effects", v)} placeholder="Type a side effect and press Enter" open={false} disabled={isGlobal} />
						</Form.Item>
					</Col>
					<Divider style={{ margin: "4px 0 12px" }}>Interactions</Divider>
					<Col span={24}>
						{!isGlobal && (
							<Space style={{ marginBottom: 8, width: "100%", flexWrap: "wrap" }}>
								<Input placeholder="Drug name" style={{ width: 180 }} value={newInteraction.name ?? ""} onChange={(e) => setNewInteraction((p) => ({ ...p, name: e.target.value }))} />
								<Select placeholder="Severity" style={{ width: 130 }} value={newInteraction.severity} onChange={(v) => setNewInteraction((p) => ({ ...p, severity: v }))} options={INTERACTION_SEVERITIES} />
								<Input placeholder="Description" style={{ width: 220 }} value={newInteraction.description ?? ""} onChange={(e) => setNewInteraction((p) => ({ ...p, description: e.target.value }))} />
								<Button icon={<PlusOutlined />} onClick={addInteraction}>
									Add
								</Button>
							</Space>
						)}
						<DataTable
							bare
							dataSource={interactions}
							rowKey={(r) => r._key}
							columns={interactionColumns}
							pagination={false}
							emptyTitle="No interactions added"
							minHeight={80}
						/>
					</Col>
				</Row>
				</Form>
			),
		},
		{
			key: "ai_context",
			label: "AI Context",
			children: (
				<Form layout="vertical" component="div">
				<Row gutter={[16, 0]}>
					<Col span={24}>
						<Form.Item label="Mechanism of Action">
							<TextArea
								value={form.mechanism_of_action}
								onChange={(e) => set("mechanism_of_action", e.target.value)}
								rows={3}
								placeholder="How the medication/peptide works biologically"
								disabled={isGlobal}
							/>
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item label="Research Notes">
							<TextArea
								value={form.research_notes}
								onChange={(e) => set("research_notes", e.target.value)}
								rows={6}
								placeholder="Research background, clinical evidence, what to look for in patient profiles"
								disabled={isGlobal}
							/>
						</Form.Item>
					</Col>
					<Col span={24}>
						<Form.Item label="Patient Profile Flags">
							{isGlobal ? (
								(form.patient_profile_flags ?? []).length === 0 ? (
									<Text type="secondary">No profile flags defined</Text>
								) : (
									<Space direction="vertical" style={{ width: "100%" }}>
										{(form.patient_profile_flags ?? []).map((flag, idx) => (
											<Tag key={idx} style={{ padding: "4px 8px" }}>
												[{flag.risk_level}] {flag.category}: {flag.name} — {flag.relevance}
											</Tag>
										))}
									</Space>
								)
							) : (
								<>
									<Space style={{ marginBottom: 8, width: "100%", flexWrap: "wrap" }}>
										<Input
											placeholder="Flag name"
											style={{ width: 160 }}
											value={newProfileFlag.name ?? ""}
											onChange={(e) => setNewProfileFlag((p) => ({ ...p, name: e.target.value }))}
										/>
										<Input
											placeholder="Category"
											style={{ width: 140 }}
											value={newProfileFlag.category ?? ""}
											onChange={(e) => setNewProfileFlag((p) => ({ ...p, category: e.target.value }))}
										/>
										<Input
											placeholder="Relevance"
											style={{ width: 200 }}
											value={newProfileFlag.relevance ?? ""}
											onChange={(e) => setNewProfileFlag((p) => ({ ...p, relevance: e.target.value }))}
										/>
										<Select
											placeholder="Risk level"
											style={{ width: 120 }}
											value={newProfileFlag.risk_level}
											onChange={(v) => setNewProfileFlag((p) => ({ ...p, risk_level: v }))}
											options={[
												{ label: "High", value: "high" },
												{ label: "Moderate", value: "moderate" },
												{ label: "Low", value: "low" },
											]}
										/>
										<Button icon={<PlusOutlined />} onClick={addProfileFlag}>
											Add
										</Button>
									</Space>
									<DataTable
										bare
										dataSource={profileFlags}
										rowKey={(r) => r._key}
										columns={profileFlagColumns}
										pagination={false}
										emptyTitle="No profile flags added"
										minHeight={80}
									/>
								</>
							)}
						</Form.Item>
					</Col>
				</Row>
				</Form>
			),
		},
		{
			key: "monograph",
			label: "Monograph",
			children: (
				<Space direction="vertical" style={{ width: "100%" }} size="large">
					<Descriptions bordered column={2} size="small">
						<Descriptions.Item label="Evidence Level" span={1}>
							{form.evidence_level ? (EVIDENCE_LEVEL_LABELS[form.evidence_level] ?? form.evidence_level) : <Text type="secondary">—</Text>}
						</Descriptions.Item>
						<Descriptions.Item label="Regulatory Pathway" span={1}>
							{form.regulatory_pathway || <Text type="secondary">—</Text>}
						</Descriptions.Item>
					</Descriptions>

					{(form.warnings ?? []).length > 0 && (
						<div>
							<Divider style={{ margin: "0 0 8px" }}>Warnings</Divider>
							<Space direction="vertical" style={{ width: "100%" }}>
								{(form.warnings ?? []).map((w, i) => (
									<Tag key={i} color="orange" style={{ whiteSpace: "normal", maxWidth: "100%" }}>{w}</Tag>
								))}
							</Space>
						</div>
					)}

					{(form.indications ?? []).length > 0 && (
						<div>
							<Divider style={{ margin: "0 0 8px" }}>Indications</Divider>
							<Descriptions bordered column={1} size="small">
								{(form.indications ?? []).map((ind, i) => (
									<Descriptions.Item key={i} label={ind.condition} span={1}>
										<Text type="secondary" style={{ fontSize: 12 }}>{ind.evidence_quality}</Text>
										{ind.notes && <div>{ind.notes}</div>}
									</Descriptions.Item>
								))}
							</Descriptions>
						</div>
					)}

					{(form.monitoring_requirements ?? []).length > 0 && (
						<div>
							<Divider style={{ margin: "0 0 8px" }}>Monitoring Requirements</Divider>
							<Descriptions bordered column={1} size="small">
								{(form.monitoring_requirements ?? []).map((m, i) => (
									<Descriptions.Item key={i} label={`${m.parameter} (${m.frequency})`} span={1}>
										{m.rationale}
									</Descriptions.Item>
								))}
							</Descriptions>
						</div>
					)}

					{form.pharmacokinetics && Object.values(form.pharmacokinetics).some(Boolean) && (
						<div>
							<Divider style={{ margin: "0 0 8px" }}>Pharmacokinetics</Divider>
							<Descriptions bordered column={2} size="small">
								{form.pharmacokinetics.half_life && <Descriptions.Item label="Half Life" span={1}>{form.pharmacokinetics.half_life}</Descriptions.Item>}
								{form.pharmacokinetics.onset_of_effects && <Descriptions.Item label="Onset of Effects" span={1}>{form.pharmacokinetics.onset_of_effects}</Descriptions.Item>}
								{form.pharmacokinetics.metabolism && <Descriptions.Item label="Metabolism" span={1}>{form.pharmacokinetics.metabolism}</Descriptions.Item>}
								{form.pharmacokinetics.excretion && <Descriptions.Item label="Excretion" span={1}>{form.pharmacokinetics.excretion}</Descriptions.Item>}
								{form.pharmacokinetics.protein_binding && <Descriptions.Item label="Protein Binding" span={1}>{form.pharmacokinetics.protein_binding}</Descriptions.Item>}
								{form.pharmacokinetics.stability && <Descriptions.Item label="Stability" span={1}>{form.pharmacokinetics.stability}</Descriptions.Item>}
								{form.pharmacokinetics.reconstitution && <Descriptions.Item label="Reconstitution" span={2}>{form.pharmacokinetics.reconstitution}</Descriptions.Item>}
							</Descriptions>
						</div>
					)}

					{form.special_populations && Object.values(form.special_populations).some(Boolean) && (
						<div>
							<Divider style={{ margin: "0 0 8px" }}>Special Populations</Divider>
							<Descriptions bordered column={2} size="small">
								{form.special_populations.pregnancy && <Descriptions.Item label="Pregnancy" span={1}>{form.special_populations.pregnancy}</Descriptions.Item>}
								{form.special_populations.lactation && <Descriptions.Item label="Lactation" span={1}>{form.special_populations.lactation}</Descriptions.Item>}
								{form.special_populations.paediatric && <Descriptions.Item label="Paediatric" span={1}>{form.special_populations.paediatric}</Descriptions.Item>}
								{form.special_populations.elderly && <Descriptions.Item label="Elderly" span={1}>{form.special_populations.elderly}</Descriptions.Item>}
								{form.special_populations.renal_impairment && <Descriptions.Item label="Renal Impairment" span={1}>{form.special_populations.renal_impairment}</Descriptions.Item>}
								{form.special_populations.hepatic_impairment && <Descriptions.Item label="Hepatic Impairment" span={1}>{form.special_populations.hepatic_impairment}</Descriptions.Item>}
							</Descriptions>
						</div>
					)}

					{(form.dosage_forms ?? []).length > 0 && (
						<div>
							<Divider style={{ margin: "0 0 8px" }}>Dosage Forms</Divider>
							<Descriptions bordered column={1} size="small">
								{(form.dosage_forms ?? []).map((d, i) => (
									<Descriptions.Item key={i} label={`${d.form}${d.concentration ? ` — ${d.concentration}` : ""}${d.volume ? ` / ${d.volume}` : ""}`} span={1}>
										{d.notes || <Text type="secondary">—</Text>}
									</Descriptions.Item>
								))}
							</Descriptions>
						</div>
					)}

					{form.combination_notes && (form.combination_notes.compatible_with?.length || form.combination_notes.synergistic_effects || form.combination_notes.combination_dosing) ? (
						<div>
							<Divider style={{ margin: "0 0 8px" }}>Combination Notes</Divider>
							<Descriptions bordered column={1} size="small">
								{(form.combination_notes.compatible_with ?? []).length > 0 && (
									<Descriptions.Item label="Compatible With" span={1}>
										<Space wrap>{(form.combination_notes.compatible_with ?? []).map((c, i) => <Tag key={i}>{c}</Tag>)}</Space>
									</Descriptions.Item>
								)}
								{form.combination_notes.synergistic_effects && (
									<Descriptions.Item label="Synergistic Effects" span={1}>{form.combination_notes.synergistic_effects}</Descriptions.Item>
								)}
								{form.combination_notes.combination_dosing && (
									<Descriptions.Item label="Combination Dosing" span={1}>{form.combination_notes.combination_dosing}</Descriptions.Item>
								)}
							</Descriptions>
						</div>
					) : null}

					{!form.evidence_level && !(form.warnings ?? []).length && !(form.indications ?? []).length && !(form.monitoring_requirements ?? []).length && (
						<Text type="secondary">No monograph data available for this medication.</Text>
					)}
				</Space>
			),
		},
	];

	return (
		<DetailDrawer
			open={open}
			onClose={onClose}
			width="70vw"
			title={isEdit ? `Edit — ${item?.name}` : "New Catalog Medication"}
			headerActions={
				!isGlobal ? (
					<Button type="primary" icon={<SaveOutlined />} loading={loading} onClick={handleSave}>
						{isEdit ? "Update" : "Create"}
					</Button>
				) : (
					<Tooltip title="Managed by the global admin system">
						<Button type="primary" icon={<SaveOutlined />} disabled>
							{isEdit ? "Update" : "Create"}
						</Button>
					</Tooltip>
				)
			}
			errorMessages={formErrors}
			onClearErrors={() => setFormErrors([])}
		>
			<Tabs activeKey={activeTab} onChange={setActiveTab} items={tabs} />
		</DetailDrawer>
	);
};

export default MedicationCatalogDrawer;
